import { useEffect, useState } from "react";
import Autocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import { COILineItemProps } from "../../../interfaces/interfaces";

export type COIChoice = {
  id?: number;
  name: string;
  partNumber?: string;
  inputValue?: string;
};

export interface COIDraftOverride {
  recordType: "inventory" | "reinstall";
  sourceRowNumber: number;
  wellId?: number;
  productId?: number;
  createWell?: { name: string };
  createProduct?: { name: string };
}

interface Props {
  row: COILineItemProps | null;
  clientName: string;
  wells: COIChoice[];
  products: COIChoice[];
  onClose: () => void;
  onSave: (override: COIDraftOverride, row: COILineItemProps) => void;
}

const filter = createFilterOptions<COIChoice>();

const addCreateOption = (
  options: COIChoice[],
  params: Parameters<typeof filter>[1],
) => {
  const filtered = filter(options, params);
  const name = params.inputValue.trim();
  if (
    name &&
    !options.some((option) => option.name.toLowerCase() === name.toLowerCase())
  ) {
    filtered.push({ name: `Create “${name}”`, inputValue: name });
  }
  return filtered;
};

const normalizedChoice = (value: COIChoice | null) =>
  value?.inputValue ? { name: value.inputValue } : value;

export const COIDraftResolveDialog = ({
  row,
  clientName,
  wells,
  products,
  onClose,
  onSave,
}: Props) => {
  const [well, setWell] = useState<COIChoice | null>(null);
  const [product, setProduct] = useState<COIChoice | null>(null);

  useEffect(() => {
    if (!row) return;
    setWell(
      wells.find((option) => option.id === row.wellId) ||
        row.well ||
        (row.fromWellRaw ? { name: row.fromWellRaw } : null),
    );
    setProduct(
      products.find((option) => option.id === row.productId) ||
        row.product ||
        null,
    );
  }, [products, row, wells]);

  const save = () => {
    if (!row || !product) return;
    const nextWell = normalizedChoice(well);
    const nextProduct = normalizedChoice(product);
    if (!nextProduct) return;
    const override: COIDraftOverride = {
      recordType: row.recordType,
      sourceRowNumber: Number(row.sourceRowNumber),
      wellId: nextWell?.id,
      productId: nextProduct.id,
      createWell:
        nextWell && !nextWell.id ? { name: nextWell.name } : undefined,
      createProduct: !nextProduct.id
        ? { name: nextProduct.name }
        : undefined,
    };
    onSave(override, {
      ...row,
      wellId: nextWell?.id || null,
      well: nextWell
        ? { id: nextWell.id || -1, name: nextWell.name }
        : null,
      productId: nextProduct.id || null,
      product: {
        id: nextProduct.id || -1,
        name: nextProduct.name,
        partNumber: nextProduct.partNumber || row.partNumber,
      },
      status: "matched",
      reviewReason: null,
    });
  };

  const productLabel = (option: COIChoice) =>
    option.inputValue ||
    (option.partNumber
      ? `${option.partNumber} · ${option.name}`
      : option.name);

  return (
    <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 850 }}>Resolve COI match</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#F7F6F1",
            border: "1px solid #E9E6DB",
            borderRadius: 2,
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={800}>
            Workbook row {row?.sourceRowNumber}
          </Typography>
          <Typography fontWeight={750}>
            {row?.partNumber || "—"} · {row?.equipmentDescription || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From {row?.fromWellRaw || "—"} · {row?.recordType === "inventory" ? "Inventory" : "REINSTALL"}
          </Typography>
        </Box>
        <TextField
          fullWidth
          value={clientName}
          label="Client"
          margin="normal"
          InputProps={{ readOnly: true }}
        />
        <Autocomplete
          value={well}
          options={wells}
          filterOptions={addCreateOption}
          getOptionLabel={(option) => option.inputValue || option.name}
          isOptionEqualToValue={(option, value) =>
            option.id != null && option.id === value.id
          }
          onChange={(_, value) => setWell(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="From well"
              margin="normal"
              helperText="Optional when the workbook From field is blank."
            />
          )}
        />
        <Autocomplete
          value={product}
          options={products}
          filterOptions={addCreateOption}
          getOptionLabel={productLabel}
          isOptionEqualToValue={(option, value) =>
            option.id != null && option.id === value.id
          }
          onChange={(_, value) => setProduct(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product"
              margin="normal"
              helperText={`Source P/N: ${row?.partNumber || "—"}`}
            />
          )}
        />
        {row?.equipmentDescription && !product?.id ? (
          <Button
            size="small"
            onClick={() =>
              setProduct({
                name: row.equipmentDescription as string,
                partNumber: row.partNumber,
                inputValue: row.equipmentDescription as string,
              })
            }
            sx={{ mt: -1.5, textTransform: "none", fontWeight: 700 }}
          >
            + Create product from workbook description
          </Button>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          disabled={!product}
          onClick={save}
          startIcon={<AutoFixHighRoundedIcon />}
        >
          Apply to draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};
